import { useEffect, useRef, useState } from "react";
import { Avatar, Button, Icon, IconButton, SkeletonText } from "@astrum/ui";
import type { Message } from "../../lib/api.js";
import { formatClock } from "../../lib/chat-format.js";
import { useRealtime } from "../../lib/realtime-context.js";
import { Composer } from "./Composer.js";
import { MessageBody } from "./MessageBody.js";
import type { ChannelMember } from "./types.js";
import type { FileUploads } from "./useFileUploads.js";
import { useThread } from "./useThread.js";

/** Files are not shared into a thread (see Composer's `allowFiles`). */
const NO_FILES: FileUploads = { uploads: [], attaching: false, attach: () => undefined, dismiss: () => undefined };

function ThreadMessage({ message, viewerId }: { message: Message; viewerId: string | undefined }) {
  if (message.deletedAt) {
    return (
      <div className="chat-threadpanel__message is-deleted">
        <p>Message deleted</p>
      </div>
    );
  }
  return (
    <div className="chat-threadpanel__message">
      <Avatar name={message.authorName} size={28} />
      <div className="chat-threadpanel__message-body">
        <div className="chat-threadpanel__message-head">
          <strong>{message.authorName}</strong>
          <time dateTime={message.createdAt}>{formatClock(message.createdAt)}</time>
          {message.alsoInChannel && <span className="chat-threadpanel__badge">Also sent to channel</span>}
        </div>
        <div className="chat-message__body">
          <MessageBody body={message.body} viewerId={viewerId} />
        </div>
      </div>
    </div>
  );
}

interface ThreadSidePanelProps {
  channelId: string;
  rootId: string;
  members: ChannelMember[];
  /** Whether the viewer belongs to the channel; others read but cannot reply. */
  canReply: boolean;
  viewerId: string | undefined;
  onClose: () => void;
}

/**
 * The reply-thread column: the root message, its replies, and a composer of
 * its own. Everything is the viewer's own copy of the thread, so following,
 * unread state and "also send to channel" never leak between people.
 */
export function ThreadSidePanel({ channelId, rootId, members, canReply, viewerId, onClose }: ThreadSidePanelProps) {
  const thread = useThread(channelId, rootId);
  const { notifyTyping } = useRealtime();
  const [alsoInChannel, setAlsoInChannel] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Each thread starts with the box unticked: a broadcast is a decision per
  // reply, not a standing preference.
  useEffect(() => setAlsoInChannel(false), [rootId]);

  // New replies keep the newest in view.
  const lastId = thread.replies[thread.replies.length - 1]?.id;
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [lastId, thread.root?.id]);

  const count = thread.root?.thread?.replyCount ?? thread.replies.length;

  return (
    <aside className="chat-threadpanel" aria-label="Thread">
      <div className="chat-details__header">
        <h3>Thread</h3>
        <div className="chat-threadpanel__actions">
          {thread.root && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => thread.setFollowing(!thread.following)}
              aria-pressed={thread.following}
              title={thread.following ? "You are notified about replies" : "Get notified about replies"}
            >
              {thread.following ? "Following" : "Follow"}
            </Button>
          )}
          <IconButton icon={<Icon name="close" size={15} />} label="Close thread" onClick={onClose} />
        </div>
      </div>

      <div className="chat-threadpanel__scroll" ref={scrollRef}>
        {thread.loading ? (
          <SkeletonText lines={4} />
        ) : thread.failed || !thread.root ? (
          <p className="chat-threadpanel__empty">This thread is no longer available.</p>
        ) : (
          <>
            <ThreadMessage message={thread.root} viewerId={viewerId} />
            <div className="chat-threadpanel__divider">
              {count} {count === 1 ? "reply" : "replies"}
            </div>
            {thread.hasMoreOlder && (
              <Button size="sm" variant="ghost" loading={thread.loadingOlder} onClick={thread.loadOlder}>
                Show earlier replies
              </Button>
            )}
            {thread.replies.map((reply) => (
              <ThreadMessage key={reply.id} message={reply} viewerId={viewerId} />
            ))}
          </>
        )}
      </div>

      {thread.root && canReply && (
        <Composer
          key={rootId}
          draft={{ channelId, threadRootId: rootId }}
          channelKind={undefined}
          placeholder="Reply in thread"
          onSend={(body) => {
            void thread.send(body, alsoInChannel);
          }}
          onTyping={() => notifyTyping(channelId)}
          onCreateTask={() => undefined}
          onCreateEvent={() => undefined}
          files={NO_FILES}
          allowFiles={false}
          replyingTo={null}
          onCancelReply={() => undefined}
          members={members}
          footer={
            <label className="chat-threadpanel__also">
              <input type="checkbox" checked={alsoInChannel} onChange={(e) => setAlsoInChannel(e.target.checked)} />
              Also send to channel
            </label>
          }
        />
      )}
    </aside>
  );
}
